import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, Settings2 } from 'lucide-react';
import { clsx } from 'clsx';
import type {
  DashboardWidgetsConfig,
  CommonWidgetId,
  PersonalWidgetId,
  WidgetDefinition,
} from '../../types';
import { COMMON_WIDGETS, PERSONAL_WIDGETS } from '../../types';

interface WidgetConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: 'common' | 'personal';
  config: DashboardWidgetsConfig;
  onSave: (config: DashboardWidgetsConfig) => void;
  showCommonTab?: boolean;
  accountType?: string;
}

export default function WidgetConfigModal({
  isOpen,
  onClose,
  activeTab,
  config,
  onSave,
  showCommonTab = true,
  accountType,
}: WidgetConfigModalProps) {
  const { t } = useTranslation('dashboard');
  const [localConfig, setLocalConfig] = useState<DashboardWidgetsConfig>(config);
  const [configTab, setConfigTab] = useState<'common' | 'personal'>(activeTab);

  // Synchroniser avec la config externe quand elle change
  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  // Synchroniser l'onglet de config avec l'onglet actif du dashboard
  useEffect(() => {
    setConfigTab(!showCommonTab ? 'personal' : activeTab);
  }, [activeTab, showCommonTab]);

  if (!isOpen) return null;

  const widgets = (configTab === 'common' ? COMMON_WIDGETS : PERSONAL_WIDGETS).filter(w => {
    // Le widget "Qui doit quoi" n'est visible que en mode personal_with_transfers
    if (w.id === 'personalPartnerDetails' && accountType !== 'personal_with_transfers') return false;
    return true;
  });

  const toggleWidget = (widgetId: string) => {
    if (configTab === 'common') {
      const commonWidgets = localConfig.common;
      const newWidgets = commonWidgets.includes(widgetId as CommonWidgetId)
        ? commonWidgets.filter((w) => w !== widgetId)
        : [...commonWidgets, widgetId as CommonWidgetId];
      setLocalConfig({ ...localConfig, common: newWidgets });
    } else {
      const personalWidgets = localConfig.personal;
      const newWidgets = personalWidgets.includes(widgetId as PersonalWidgetId)
        ? personalWidgets.filter((w) => w !== widgetId)
        : [...personalWidgets, widgetId as PersonalWidgetId];
      setLocalConfig({ ...localConfig, personal: newWidgets });
    }
  };

  const handleSave = () => {
    onSave(localConfig);
    onClose();
  };

  const handleCancel = () => {
    setLocalConfig(config);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Settings2 className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                {t('common.customizeDashboard')}
              </h2>
            </div>
            <button
              onClick={handleCancel}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 px-6">
            {showCommonTab && (
              <button
                onClick={() => setConfigTab('common')}
                className={clsx(
                  'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                  configTab === 'common'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                {t('widgets.commonTab')}
              </button>
            )}
            <button
              onClick={() => setConfigTab('personal')}
              className={clsx(
                'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                configTab === 'personal'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              {t('widgets.personalTab')}
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[50vh]">
            <p className="text-sm text-gray-600 mb-4">
              {t('common.selectModules')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {widgets.map((widget: WidgetDefinition) => {
                const isActive = configTab === 'common'
                  ? localConfig.common.includes(widget.id as CommonWidgetId)
                  : localConfig.personal.includes(widget.id as PersonalWidgetId);
                return (
                  <button
                    key={widget.id}
                    onClick={() => toggleWidget(widget.id)}
                    className={clsx(
                      'flex items-start gap-3 p-4 rounded-lg border-2 transition-all text-left',
                      isActive
                        ? configTab === 'common'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    )}
                  >
                    <div
                      className={clsx(
                        'flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                        isActive
                          ? configTab === 'common'
                            ? 'bg-primary-600 border-primary-600'
                            : 'bg-emerald-600 border-emerald-600'
                          : 'border-gray-300'
                      )}
                    >
                      {isActive && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={clsx(
                          'font-medium text-sm',
                          isActive ? 'text-gray-900' : 'text-gray-700'
                        )}
                      >
                        {t(`widgetNames.${widget.name}`)}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {t(`widgetNames.${widget.description}`)}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            >
              {t('cancel', { ns: 'common' })}
            </button>
            <button
              onClick={handleSave}
              className={clsx(
                'px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors',
                configTab === 'common'
                  ? 'bg-primary-600 hover:bg-primary-700'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              )}
            >
              {t('save', { ns: 'common' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
